import React, { useState, useEffect } from 'react'
import { Table, Button, Input, Select, Space, Tag, Card, Statistic, Row, Col, Modal, Form, message, Drawer, Tooltip, Switch } from 'antd'
import { EditOutlined, EyeOutlined, CreditCardOutlined, DollarOutlined, RiseOutlined, PlayCircleOutlined } from '@ant-design/icons'
import { api } from '../services/api'

const { Search } = Input
const { Option } = Select

interface CompanySubscription {
  id: string
  businessId: string
  businessName: string
  businessEmail?: string
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  plan: 'starter' | 'professional' | 'enterprise' | 'custom'
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete'
  billingCycle: 'monthly' | 'yearly'
  currentPeriodStart: string
  currentPeriodEnd: string
  trialEnd?: string
  cancelAtPeriodEnd: boolean
  mrr: number // Monthly Recurring Revenue
  arr: number // Annual Recurring Revenue
  totalRevenue: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  usage: {
    users: number
    maxUsers: number
    customers: number
    jobs: number
    invoices: number
    storageUsed: number
    maxStorage: number
  }
  billing: {
    lastPaymentDate?: string
    lastPaymentAmount?: number
    nextBillingDate?: string
    paymentMethod?: string
    billingEmail?: string
  }
}

interface CompanySubscriptionStats {
  total: number
  active: number
  trialing: number
  pastDue: number
  canceled: number
  totalMRR: number
  totalARR: number
  averageRevenuePerUser: number
  churnRate: number
  byPlan: Record<string, { count: number; revenue: number }>
  revenueGrowth: number
  newSignups: number
}

const CompanySubscriptionsPage: React.FC = () => {
  console.log('CompanySubscriptionsPage component loaded')
  const [subscriptions, setSubscriptions] = useState<CompanySubscription[]>([])
  const [stats, setStats] = useState<CompanySubscriptionStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    plan: '',
    billingCycle: ''
  })
  const [selectedSubscription, setSelectedSubscription] = useState<CompanySubscription | null>(null)
  const [subscriptionDrawerVisible, setSubscriptionDrawerVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editType, setEditType] = useState<'plan' | 'status' | 'billing' | null>(null)
  const [form] = Form.useForm()

  const fetchSubscriptions = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: pagination.current.toString(),
        limit: pagination.pageSize.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.status && { status: filters.status }),
        ...(filters.plan && { plan: filters.plan }),
        ...(filters.billingCycle && { billingCycle: filters.billingCycle })
      })

      const response = await api.get(`/company-subscriptions?${params}`)
      setSubscriptions(response.data.subscriptions)
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }))
    } catch (error) {
      console.error('Failed to fetch company subscriptions:', error)
      message.error('Failed to load company subscriptions')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await api.get('/company-subscriptions/stats')
      setStats(response.data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  useEffect(() => {
    fetchSubscriptions()
    fetchStats()
  }, [pagination.current, pagination.pageSize, filters])

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }))
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleTableChange = (paginationConfig: any) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }))
  }

  const handleViewSubscription = (subscription: CompanySubscription) => {
    setSelectedSubscription(subscription)
    setSubscriptionDrawerVisible(true)
  }

  const handleEditPlan = (subscription: CompanySubscription) => {
    setSelectedSubscription(subscription)
    setEditType('plan')
    form.setFieldsValue({
      plan: subscription.plan,
      billingCycle: subscription.billingCycle
    })
    setEditModalVisible(true)
  }

  const handleEditStatus = (subscription: CompanySubscription) => {
    setSelectedSubscription(subscription)
    setEditType('status')
    form.setFieldsValue({
      status: subscription.status,
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd
    })
    setEditModalVisible(true)
  }

  const handleEditBilling = (subscription: CompanySubscription) => {
    setSelectedSubscription(subscription)
    setEditType('billing')
    form.setFieldsValue({
      billingEmail: subscription.billing.billingEmail,
      paymentMethod: subscription.billing.paymentMethod
    })
    setEditModalVisible(true)
  }

  const handleSaveEdit = async (values: any) => {
    if (!selectedSubscription || !editType) return

    try {
      if (editType === 'plan') {
        await api.put(`/company-subscriptions/${selectedSubscription.businessId}/plan`, {
          plan: values.plan,
          billingCycle: values.billingCycle
        })
        message.success('Subscription plan updated successfully')
      } else if (editType === 'status') {
        await api.put(`/company-subscriptions/${selectedSubscription.businessId}/status`, {
          status: values.status,
          cancelAtPeriodEnd: values.cancelAtPeriodEnd
        })
        message.success('Subscription status updated successfully')
      } else if (editType === 'billing') {
        await api.put(`/company-subscriptions/${selectedSubscription.businessId}/billing`, {
          billingEmail: values.billingEmail,
          paymentMethod: values.paymentMethod
        })
        message.success('Billing information updated successfully')
      }

      setEditModalVisible(false)
      setEditType(null)
      form.resetFields()
      fetchSubscriptions()
      fetchStats()
    } catch (error) {
      console.error('Failed to update subscription:', error)
      message.error('Failed to update subscription')
    }
  }

  const getStatusTag = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return <Tag color="orange">Canceling</Tag>
    }
    
    switch (status) {
      case 'active':
        return <Tag color="green">Active</Tag>
      case 'trialing':
        return <Tag color="blue">Trial</Tag>
      case 'past_due':
        return <Tag color="orange">Past Due</Tag>
      case 'canceled':
        return <Tag color="red">Canceled</Tag>
      case 'unpaid':
        return <Tag color="red">Unpaid</Tag>
      case 'incomplete':
        return <Tag color="yellow">Incomplete</Tag>
      default:
        return <Tag color="default">{status}</Tag>
    }
  }

  const getPlanTag = (plan: string) => {
    const colors = {
      starter: 'blue',
      professional: 'green',
      enterprise: 'purple',
      custom: 'gold'
    }
    return <Tag color={colors[plan as keyof typeof colors] || 'default'}>{plan.toUpperCase()}</Tag>
  }

  const getBillingCycleTag = (cycle: string) => {
    return cycle === 'yearly' ? 
      <Tag color="green">Yearly</Tag> : 
      <Tag color="blue">Monthly</Tag>
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const columns = [
    {
      title: 'Company',
      dataIndex: 'businessName',
      key: 'businessName',
      render: (name: string, record: CompanySubscription) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          {record.businessEmail && <div style={{ fontSize: '12px', color: '#666' }}>{record.businessEmail}</div>}
          {record.stripeCustomerId && <div style={{ fontSize: '10px', color: '#999' }}>ID: {record.stripeCustomerId}</div>}
        </div>
      )
    },
    {
      title: 'Plan & Billing',
      key: 'planBilling',
      render: (record: CompanySubscription) => (
        <div>
          <div>{getPlanTag(record.plan)}</div>
          <div style={{ marginTop: '4px' }}>{getBillingCycleTag(record.billingCycle)}</div>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: CompanySubscription) => getStatusTag(record.status, record.cancelAtPeriodEnd)
    },
    {
      title: 'Revenue',
      key: 'revenue',
      render: (record: CompanySubscription) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
            MRR: {formatCurrency(record.mrr)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ARR: {formatCurrency(record.arr)}
          </div>
        </div>
      )
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (record: CompanySubscription) => (
        <div style={{ fontSize: '12px' }}>
          <div>Users: {record.usage.users}/{record.usage.maxUsers}</div>
          <div>Jobs: {record.usage.jobs}</div>
          <div>Storage: {Math.round(record.usage.storageUsed / 1024)}MB/{Math.round(record.usage.maxStorage / 1024)}MB</div>
        </div>
      )
    },
    {
      title: 'Next Billing',
      dataIndex: 'currentPeriodEnd',
      key: 'nextBilling',
      render: (date: string) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: CompanySubscription) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewSubscription(record)}
            />
          </Tooltip>
          <Tooltip title="Change Plan">
            <Button
              type="text"
              icon={<CreditCardOutlined />}
              onClick={() => handleEditPlan(record)}
            />
          </Tooltip>
          <Tooltip title="Manage Status">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditStatus(record)}
            />
          </Tooltip>
          <Tooltip title="Billing Settings">
            <Button
              type="text"
              icon={<DollarOutlined />}
              onClick={() => handleEditBilling(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        style={{ 
          marginBottom: '24px', 
          background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
          border: 'none'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{ textAlign: 'center', color: 'white' }}>
          <h1 style={{ color: 'white', margin: 0, fontSize: '28px', fontWeight: 600 }}>Company Subscription Management</h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '8px 0 0 0', fontSize: '16px' }}>
            Manage SaaS subscriptions for companies using your platform
          </p>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: '4px 0 0 0', fontSize: '14px' }}>
            Track revenue, billing, and subscription lifecycle
          </p>
        </div>
      </Card>

      {/* Revenue Statistics Cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={12} sm={12} md={8} lg={4} xl={4}>
            <Card bodyStyle={{ padding: '16px' }}>
              <Statistic 
                title="Companies" 
                value={stats.total}
                prefix={<RiseOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ fontSize: '24px', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4} xl={4}>
            <Card bodyStyle={{ padding: '16px' }}>
              <Statistic 
                title="Active" 
                value={stats.active} 
                valueStyle={{ color: '#3f8600', fontSize: '24px', fontWeight: 600 }}
                prefix={<PlayCircleOutlined style={{ color: '#3f8600' }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4} xl={4}>
            <Card bodyStyle={{ padding: '16px' }}>
              <Statistic 
                title="Monthly Revenue" 
                value={stats.totalMRR}
                formatter={(value) => formatCurrency(value as number)}
                valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: 600 }}
                prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4} xl={4}>
            <Card bodyStyle={{ padding: '16px' }}>
              <Statistic 
                title="Annual Revenue" 
                value={stats.totalARR}
                formatter={(value) => formatCurrency(value as number)}
                valueStyle={{ color: '#722ed1', fontSize: '20px', fontWeight: 600 }}
                prefix={<RiseOutlined style={{ color: '#722ed1' }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4} xl={4}>
            <Card bodyStyle={{ padding: '16px' }}>
              <Statistic 
                title="Avg/User" 
                value={stats.averageRevenuePerUser}
                formatter={(value) => formatCurrency(value as number)}
                valueStyle={{ color: '#13c2c2', fontSize: '20px', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} md={8} lg={4} xl={4}>
            <Card bodyStyle={{ padding: '16px' }}>
              <Statistic 
                title="Churn Rate" 
                value={stats.churnRate}
                suffix="%"
                valueStyle={{ 
                  color: stats.churnRate > 5 ? '#cf1322' : '#3f8600', 
                  fontSize: '24px', 
                  fontWeight: 600 
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Plan Revenue Breakdown */}
      {stats && Object.keys(stats.byPlan).length > 0 && (
        <Card title="Revenue by Plan" style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            {Object.entries(stats.byPlan).map(([plan, data]) => (
              <Col xs={24} sm={12} md={8} lg={6} key={plan}>
                <Card 
                  size="small" 
                  bodyStyle={{ padding: '16px', textAlign: 'center' }}
                >
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#1890ff', marginBottom: '8px' }}>
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                    {data.count} {data.count === 1 ? 'company' : 'companies'}
                  </div>
                  
                  {/* Monthly Revenue */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '18px', fontWeight: 600, color: '#1890ff' }}>
                      {formatCurrency(data.revenue)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                      Monthly
                    </div>
                  </div>
                  
                  {/* Annual Revenue */}
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#722ed1' }}>
                      {formatCurrency(data.revenue * 12)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                      Annual
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* Billing Cycle Breakdown */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Card title="Monthly Billing" bodyStyle={{ padding: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 600, color: '#1890ff', marginBottom: '8px' }}>
                  {subscriptions.filter(s => s.billingCycle === 'monthly' && s.isActive).length}
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  Monthly Subscribers
                </div>
                <div style={{ fontSize: '20px', fontWeight: 600, color: '#1890ff' }}>
                  {formatCurrency(
                    subscriptions
                      .filter(s => s.billingCycle === 'monthly' && s.isActive)
                      .reduce((sum, s) => sum + s.mrr, 0)
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  Monthly Revenue
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Card title="Yearly Billing" bodyStyle={{ padding: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 600, color: '#722ed1', marginBottom: '8px' }}>
                  {subscriptions.filter(s => s.billingCycle === 'yearly' && s.isActive).length}
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  Yearly Subscribers
                </div>
                <div style={{ fontSize: '20px', fontWeight: 600, color: '#722ed1' }}>
                  {formatCurrency(
                    subscriptions
                      .filter(s => s.billingCycle === 'yearly' && s.isActive)
                      .reduce((sum, s) => sum + s.mrr, 0)
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  Monthly Revenue
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={12} lg={8} xl={8}>
            <Search
              placeholder="Search companies..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
              size="large"
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: '100%' }}
              size="large"
              onChange={(value) => handleFilterChange('status', value || '')}
            >
              <Option value="active">Active</Option>
              <Option value="trialing">Trial</Option>
              <Option value="past_due">Past Due</Option>
              <Option value="canceled">Canceled</Option>
              <Option value="unpaid">Unpaid</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Select
              placeholder="Plan"
              allowClear
              style={{ width: '100%' }}
              size="large"
              onChange={(value) => handleFilterChange('plan', value || '')}
            >
              <Option value="starter">Starter</Option>
              <Option value="professional">Professional</Option>
              <Option value="enterprise">Enterprise</Option>
              <Option value="custom">Custom</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Select
              placeholder="Billing Cycle"
              allowClear
              style={{ width: '100%' }}
              size="large"
              onChange={(value) => handleFilterChange('billingCycle', value || '')}
            >
              <Option value="monthly">Monthly</Option>
              <Option value="yearly">Yearly</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Company Subscriptions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={subscriptions}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} company subscriptions`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Subscription Details Drawer */}
      <Drawer
        title="Company Subscription Details"
        width={700}
        open={subscriptionDrawerVisible}
        onClose={() => setSubscriptionDrawerVisible(false)}
      >
        {selectedSubscription && (
          <div>
            <Card title="Company Information" style={{ marginBottom: '16px' }}>
              <p><strong>Name:</strong> {selectedSubscription.businessName}</p>
              <p><strong>Email:</strong> {selectedSubscription.businessEmail || 'Not provided'}</p>
              <p><strong>Stripe Customer ID:</strong> {selectedSubscription.stripeCustomerId || 'Not connected'}</p>
              <p><strong>Subscription ID:</strong> {selectedSubscription.stripeSubscriptionId || 'Not connected'}</p>
            </Card>

            <Card title="Subscription Details" style={{ marginBottom: '16px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <p><strong>Plan:</strong> {getPlanTag(selectedSubscription.plan)}</p>
                  <p><strong>Status:</strong> {getStatusTag(selectedSubscription.status, selectedSubscription.cancelAtPeriodEnd)}</p>
                  <p><strong>Billing Cycle:</strong> {getBillingCycleTag(selectedSubscription.billingCycle)}</p>
                </Col>
                <Col span={12}>
                  <p><strong>Current Period:</strong> {new Date(selectedSubscription.currentPeriodStart).toLocaleDateString()} - {new Date(selectedSubscription.currentPeriodEnd).toLocaleDateString()}</p>
                  {selectedSubscription.trialEnd && (
                    <p><strong>Trial Ends:</strong> {new Date(selectedSubscription.trialEnd).toLocaleDateString()}</p>
                  )}
                  <p><strong>Cancel at Period End:</strong> {selectedSubscription.cancelAtPeriodEnd ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </Card>

            <Card title="Revenue Metrics" style={{ marginBottom: '16px' }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic title="MRR" value={selectedSubscription.mrr} formatter={(value) => formatCurrency(value as number)} />
                </Col>
                <Col span={8}>
                  <Statistic title="ARR" value={selectedSubscription.arr} formatter={(value) => formatCurrency(value as number)} />
                </Col>
                <Col span={8}>
                  <Statistic title="Total Revenue" value={selectedSubscription.totalRevenue} formatter={(value) => formatCurrency(value as number)} />
                </Col>
              </Row>
            </Card>

            <Card title="Usage Summary" style={{ marginBottom: '16px' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="Users" value={`${selectedSubscription.usage.users}/${selectedSubscription.usage.maxUsers}`} />
                  <Statistic title="Customers" value={selectedSubscription.usage.customers} />
                </Col>
                <Col span={12}>
                  <Statistic title="Jobs" value={selectedSubscription.usage.jobs} />
                  <Statistic title="Storage" value={`${Math.round(selectedSubscription.usage.storageUsed/1024)}MB/${Math.round(selectedSubscription.usage.maxStorage/1024)}MB`} />
                </Col>
              </Row>
            </Card>

            <Card title="Billing Information">
              {selectedSubscription.billing.lastPaymentDate && (
                <p><strong>Last Payment:</strong> {formatCurrency(selectedSubscription.billing.lastPaymentAmount || 0)} on {new Date(selectedSubscription.billing.lastPaymentDate).toLocaleDateString()}</p>
              )}
              {selectedSubscription.billing.nextBillingDate && (
                <p><strong>Next Billing:</strong> {new Date(selectedSubscription.billing.nextBillingDate).toLocaleDateString()}</p>
              )}
              <p><strong>Payment Method:</strong> {selectedSubscription.billing.paymentMethod || 'Not set'}</p>
              <p><strong>Billing Email:</strong> {selectedSubscription.billing.billingEmail || 'Same as company email'}</p>
            </Card>
          </div>
        )}
      </Drawer>

      {/* Edit Modals */}
      <Modal
        title={
          editType === 'plan' ? 'Change Subscription Plan' : 
          editType === 'status' ? 'Manage Subscription Status' : 
          'Update Billing Information'
        }
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
          setEditType(null)
          form.resetFields()
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveEdit}
        >
          {editType === 'plan' ? (
            <>
              <Form.Item
                name="plan"
                label="Subscription Plan"
                rules={[{ required: true, message: 'Please select a plan' }]}
              >
                <Select>
                  <Option value="starter">Starter</Option>
                  <Option value="professional">Professional</Option>
                  <Option value="enterprise">Enterprise</Option>
                  <Option value="custom">Custom</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="billingCycle"
                label="Billing Cycle"
                rules={[{ required: true, message: 'Please select billing cycle' }]}
              >
                <Select>
                  <Option value="monthly">Monthly</Option>
                  <Option value="yearly">Yearly</Option>
                </Select>
              </Form.Item>
            </>
          ) : editType === 'status' ? (
            <>
              <Form.Item
                name="status"
                label="Subscription Status"
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="trialing">Trial</Option>
                  <Option value="past_due">Past Due</Option>
                  <Option value="canceled">Canceled</Option>
                  <Option value="unpaid">Unpaid</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="cancelAtPeriodEnd"
                valuePropName="checked"
                label="Cancel at Period End"
              >
                <Switch />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="billingEmail"
                label="Billing Email"
                rules={[{ type: 'email', message: 'Please enter a valid email' }]}
              >
                <Input placeholder="billing@company.com" />
              </Form.Item>
              <Form.Item
                name="paymentMethod"
                label="Payment Method"
              >
                <Input placeholder="Card ending in 1234" />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false)
                setEditType(null)
                form.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default CompanySubscriptionsPage
